import * as React from "react";

import { CONTROL_RADIUS, cx } from "./controlStyles";

// Text fields on the sunken inset surface with a visible 1px boundary.
// Focus (click or keyboard — a text field always shows where typing
// goes) turns the border to the focus colour with a soft halo.
// Placeholder uses the subtle AA text colour.
//
// `fieldSize` (not `size`, which is a native <input> attribute):
// sm 28 / md 32 (default) / lg 40px. 44px min on touch screens. On a
// Textarea it sets the starting height and text size (sm 64px / md 80px /
// lg 120px).

const base = cx(
  "w-full min-w-0 border border-control bg-inset px-3 text-sm text-fg",
  CONTROL_RADIUS,
  "placeholder:text-fg-subtle",
  "[@media(hover:hover)]:hover:border-fg-subtle",
  "focus:outline-none focus:border-[color:var(--c-focus)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--c-focus)_25%,transparent)]",
  "aria-[invalid=true]:border-[color:var(--c-danger)]",
  "disabled:cursor-not-allowed disabled:opacity-45",
  "transition-[border-color,box-shadow] duration-100",
);

const HEIGHT = {
  sm: "h-[28px]",
  md: "h-[32px]",
  lg: "h-[40px] text-base",
} as const;

export type FieldSize = keyof typeof HEIGHT;

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  fieldSize?: FieldSize;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, fieldSize = "md", ...props }, ref) => (
    <input
      type={type}
      ref={ref}
      className={cx(base, HEIGHT[fieldSize], "[@media(pointer:coarse)]:min-h-[44px]", className)}
      {...props}
    />
  ),
);
Input.displayName = "Input";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  fieldSize?: FieldSize;
}

const AREA = {
  sm: "min-h-[64px] text-sm",
  md: "min-h-[80px]",
  lg: "min-h-[120px] text-base",
} as const;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, fieldSize = "md", ...props }, ref) => (
    <textarea
      ref={ref}
      className={cx(base, AREA[fieldSize], "resize-none py-2 leading-relaxed", className)}
      {...props}
    />
  ),
);
Textarea.displayName = "Textarea";
